import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Tables } from './../../../../Common/Tables'
import { Search } from './../../../../Common/Tables/Search'
//import { InstructorBase } from './../../../../Layouts/Instructor/InstructorBase'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Details } from './../../../../Common/Modals/Details'
import { Confirm } from './../../../../Common/Modals/Confirm'
import { GetAgendas, DeleteAgenda as BLDeleteAgenda, RescheduleAgenda as BLRescheduleAgenda, ConvertToInputDate } from './../../../../../utilities/BL/Instructor/Agendas'
import { Loading } from './../../../../Common/Loading'

import { LogErrors } from './../../../../../utilities/LogErrors'
import { usePersistedState } from './../../../../../utilities/usePersistedState'
import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'


/**
 * Texto con el que se muestra (y filtra) el curso de una evaluación: "Curso (Nivel X)".
 *
 * @param {Object} row - Evaluación agendada.
 * @returns {string|null} Texto del curso, o null si la evaluación no tiene curso.
 */
const GetCourseLabel = (row) => (
    row?.courseName ? `${row.courseName}${row?.levelName ? ` (Nivel ${row.levelName})` : ''}`.trim() : null
)

/**
 * Indica si una evaluación cumple las selecciones de las listas Curso, Grupo y Tipo.
 * `ignoredKey` omite una de ellas ('course' | 'group' | 'type'): se usa para calcular las
 * opciones de esa lista a partir de lo que eligieron las otras dos (filtro cruzado).
 *
 * @param {Object} row - Evaluación agendada.
 * @param {{ course: string|null, group: string|null, type: string|null }} filters - Selecciones actuales.
 * @param {string|null} ignoredKey - Lista cuya selección no se toma en cuenta.
 * @returns {boolean}
 */
const RowMatchesSelects = (row, filters, ignoredKey = null) => {
    if (ignoredKey !== 'course' && filters.course && GetCourseLabel(row) !== filters.course) return false
    if (ignoredKey !== 'group' && filters.group && row?.groupName !== filters.group) return false
    if (ignoredKey !== 'type' && filters.type && row?.typeEvaluation !== filters.type) return false

    return true
}

/**
 * Valores únicos (sin vacíos) ordenados alfabéticamente.
 *
 * @param {Array} values
 * @returns {Array}
 */
const UniqueSorted = (values) => Array.from(new Set(values.filter(Boolean))).sort()


const Agenda = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    const [tableConfig,setTableConfig] = useState(null)
    const [isLoading,setIsLoading] = useState(true)

    const [showDetailsModal,setShowDetailsModal] = useState(false)
    const [showConfirmationModal,setShowConfirmationModal] = useState(false)
    const [actionTypeOfModal,setActionTypeOfModal] = useState(null)

    const [agendaDetails,setAgendaDetails] = useState(null)
    const [agendaConfirmDetails,setAgendaConfirmDetails] = useState(null)

    const [rescheduleAgendaDetails,setRescheduleAgendaDetails] = useState(null)
    const [rescheduleAgendaModal,setRescheduleAgendaModal] = useState(null)

    const [massiveSummary, setMassiveSummary] = useState(null)
    const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)
    
    const [showPreviousStepButton, setShowPreviousStepButton] = useState(false)
    const [showNextStepButton, setShowNextStepButton] = useState(false)

    // Los filtros y la búsqueda se conservan mientras dure la sesión (al ir a "Agendar NUEVA
    // evaluación" y regresar, la página recupera lo último que se había aplicado).
    const [quickRange, setQuickRange] = usePersistedState('agenda.quickRange', null) // null | 'today' | 'm1' | 'm6' | 'm12'

    const [currentIndexInFiltered, setCurrentIndexInFiltered] = useState(-1)
    
    //--- Commons to Table and Search ---//
    const [allRows, setAllRows] = useState([])
    const [searchText, setSearchText] = useState('')

    const [filteredRows, setFilteredRows] = useState(allRows)

    // === Navegación sobre lista visible ===
    const filteredRowsRef = useRef([])
    useEffect(() => { filteredRowsRef.current = filteredRows }, [filteredRows])
    
    const allRowsRef = useRef([])

    // --- Filtros (NUEVO) ---
    const [textRaw, setTextRaw] = usePersistedState('agenda.search', '')          // lo que escribe el usuario
    const [textDebounced, setTextDebounced] = useState(textRaw) // texto con debounce (arranca igual que el texto conservado)
    const [filters, setFilters] = usePersistedState('agenda.filters', {
        course: null,
        group: null,
        type: null,
        dateFrom: '',
        dateTo: ''
    })

    useEffect(() => {
        allRowsRef.current = allRows
    }, [allRows])
    
    useEffect(() => {
        const filtered = allRows.filter((row) =>
            row.name.toLowerCase().includes(searchText.toLowerCase())
        )

        setFilteredRows(filtered)
    }, [searchText, allRows])

    // Reemplaza la función handleSearch existente por esta:
    const handleSearch = async (value) => {
        try {
            setTextRaw(value ?? '')
        } catch (error) {
            await LogErrors('handleSearch - Agenda', error)
        }
    }

    // Debounce: aplica el texto 300 ms después de que el usuario deja de teclear
    useEffect(() => {
        const t = setTimeout(() => setTextDebounced((textRaw ?? '').trim()), 300)
        return () => clearTimeout(t)
    }, [textRaw])

    // Opciones únicas (Curso / Grupo / Tipo) calculadas desde allRows
    // const uniqueCourses = React.useMemo(() => {
    //     const s = new Set((allRows ?? []).map(r => r.courseName).filter(Boolean))
    //     return Array.from(s).sort()
    // }, [allRows])    
    // Filtro cruzado: cada lista muestra solo los valores que existen en las evaluaciones que
    // cumplen las selecciones de las otras dos listas (se ignora la selección de la propia lista).
    const uniqueCourses = React.useMemo(() => {
        const rows = (allRows ?? []).filter(r => RowMatchesSelects(r, filters, 'course'))
        return UniqueSorted(rows.map(GetCourseLabel))
    }, [allRows, filters])

    const uniqueGroups = React.useMemo(() => {
        const rows = (allRows ?? []).filter(r => RowMatchesSelects(r, filters, 'group'))
        return UniqueSorted(rows.map(r => r.groupName))
    }, [allRows, filters])

    const uniqueTypes = React.useMemo(() => {
        const rows = (allRows ?? []).filter(r => RowMatchesSelects(r, filters, 'type'))
        return UniqueSorted(rows.map(r => r.typeEvaluation))
    }, [allRows, filters])

    // Si el listado cambia (por ejemplo, al eliminar o reprogramar una evaluación) y una
    // selección deja de existir en su lista, se limpia para no dejar la tabla vacía sin explicación.
    useEffect(() => {
        // Mientras carga el listado todavía no hay opciones: no se limpian los filtros conservados
        if (isLoading) return

        setFilters(f => {
            const courseIsValid = !f.course || uniqueCourses.includes(f.course)
            const groupIsValid = !f.group || uniqueGroups.includes(f.group)
            const typeIsValid = !f.type || uniqueTypes.includes(f.type)

            if (courseIsValid && groupIsValid && typeIsValid) return f

            return {
                ...f,
                course: courseIsValid ? f.course : null,
                group: groupIsValid ? f.group : null,
                type: typeIsValid ? f.type : null
            }
        })
    }, [isLoading, uniqueCourses, uniqueGroups, uniqueTypes])

    // 'YYYY-MM-DD' o '' si no puede
    const toISODate = (s) => {
        if (!s) return ''
        const m = String(s).trim().match(/^(\d{4}-\d{2}-\d{2})/)
        return m ? m[1] : ''
    }

    // 'HH:mm:ss' normalizado (acepta 'HH:mm')
    const toISOTime = (t) => {
        if (!t) return '00:00:00'
        const parts = String(t).trim().split(':')
        if (parts.length === 2) return `${parts[0].padStart(2,'0')}:${parts[1].padStart(2,'0')}:00`
        if (parts.length === 3) return `${parts[0].padStart(2,'0')}:${parts[1].padStart(2,'0')}:${parts[2].padStart(2,'0')}`
        return '00:00:00'
    }

    // Date desde fecha + hora (en local)
    const makeDate = (dateStr, timeStr) => {
        const d = toISODate(dateStr)
        const h = toISOTime(timeStr)
        if (!d) return null
        // Se construye como 'YYYY-MM-DDTHH:mm:ss' (local interpretado por el motor)
        const dt = new Date(`${d}T${h}`)
        return isNaN(dt.getTime()) ? null : dt
    }

    // Inicio/fin de día
    const startOfDay = (dateStr) => makeDate(dateStr, '00:00:00')
    const endOfDay   = (dateStr) => makeDate(dateStr, '23:59:59')

    // Suma/resta meses (negativos para atrás)
    const addMonths = (base, delta) => {
        const d = new Date(base.getTime())
        d.setMonth(d.getMonth() + delta)
        return d
    }

    // Util: truncar a inicio/fin de día una instancia Date
    const asStartOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0,0,0,0)
    const asEndOfDay   = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23,59,59,999)

    useEffect(() => {
        const rows = Array.isArray(allRowsRef.current) ? allRowsRef.current : []
        const text = (textDebounced ?? '').toLowerCase()

        // 1) Rango por preset
        const now = new Date()
        let quickFromDate = null
        let quickToDate   = null

        if (quickRange === 'today') {
            quickFromDate = asStartOfDay(now)
            quickToDate   = asEndOfDay(now)
        } else if (quickRange === 'm1') {
            quickFromDate = asStartOfDay(addMonths(now, -1))
            quickToDate   = asEndOfDay(now)
        } else if (quickRange === 'm6') {
            quickFromDate = asStartOfDay(addMonths(now, -6))
            quickToDate   = asEndOfDay(now)
        } else if (quickRange === 'm12') {
            quickFromDate = asStartOfDay(addMonths(now, -12))
            quickToDate   = asEndOfDay(now)
        }

        // 2) Periodo personalizado (si el usuario selecciona, anulamos preset)
        const customFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null
        const customTo   = filters.dateTo   ? endOfDay(filters.dateTo)   : null

        const from = customFrom ?? quickFromDate
        const to   = customTo   ?? quickToDate

        // 3) Filtrado
        const filtered = rows.filter(r => {
            // Texto libre
            if (text) {
            const hay = [
                r?.itemName ?? '',
                GetCourseLabel(r) ?? '',
                r?.groupName ?? ''
            ].some(v => String(v).toLowerCase().includes(text))
            if (!hay) return false
            }

            // Selects (Curso / Grupo / Tipo)
            if (!RowMatchesSelects(r, filters)) return false

            // === Ventana de agenda ===
            // Fila puede traer: startDate, startHour, finishDate, finishHour
            // Si faltan, hacemos fallback sensato.

            const rowStart =
            makeDate(r?.startDate, r?.startHour) ??
            (r?.date ? startOfDay(r.date) : null)

            const rowEnd =
            makeDate(r?.finishDate, r?.finishHour) ??
            (r?.date ? endOfDay(r.date) : null)

            // Si no hay ninguna referencia de fecha, no la descartamos por fecha.
            if (!rowStart && !rowEnd) return true

            // Cohesiona: si solo hay start → end = mismo día fin de día; si solo hay end → start = mismo día inicio
            const _rowStart = rowStart ?? (rowEnd ? asStartOfDay(rowEnd) : null)
            const _rowEnd   = rowEnd   ?? (rowStart ? asEndOfDay(rowStart) : null)

            // Si no hay rango elegido (from/to ambos nulos) → no filtra por fecha
            if (!from && !to) return true

            // Intersección inclusiva: [rowStart,rowEnd] ∩ [from,to] ≠ ∅
            // Reglas:
            //  - Si 'from' existe: la fila debe terminar en o después de 'from' → _rowEnd >= from
            //  - Si 'to' existe:   la fila debe empezar en o antes de 'to'    → _rowStart <= to
            if (from && _rowEnd && _rowEnd < from) return false
            if (to   && _rowStart && _rowStart > to) return false

            return true
        })

        setFilteredRows(filtered)
    }, [allRows, textDebounced, filters, quickRange])

    //--- Commons to Table and Search ---//
    

    //--- Calls to API ----//

    // Extrae identificadores requeridos por la BL desde un row con nombres variados
    const GetAgendaIdsFromRow = (row) => {
        try {
            const idAgenda = Number(row.id ?? row.Id ?? row.idAgenda ?? row.IdAgenda)
            const idGroup  = Number(row.idGroup ?? row.groupId ?? row.IdGroup ?? row.Id_Group)
            const idCourse = Number(row.idCourse ?? row.courseId ?? row.IdCourse ?? row.Id_Course)
            const idItem   = Number(row.idItem ?? row.IdItem ?? row.itemId ?? row.Id_Item)

            return { idAgenda, idGroup, idCourse, idItem }
        } catch (error) {
            
        }
    }

    // Checa si una fila con ese idAgenda todavía existe en una lista
    const ExistsByAgendaId = (rows, idAgenda) => {
        try {
            if (!Array.isArray(rows)) return false

            return rows.some(r => {
                const rid = Number(r.id ?? r.Id ?? r.idAgenda ?? r.IdAgenda)

                return Number(rid) === Number(idAgenda)
            })
        } catch (error) {
            
        }
    }

    const DeleteAgenda = async () => {
        try {
            if (!agendaConfirmDetails) return

            // 1) Obtener IDs requeridos por la BL
            const { idAgenda, idGroup, idCourse, idItem } = GetAgendaIdsFromRow(agendaConfirmDetails)

            // 2) Llamar a la BL con los args correctos + el listado actual
            const response = await BLDeleteAgenda(idAgenda, idGroup, idCourse, idItem, allRowsRef.current)

            if (response.success) {
                setAllRows(response.data)
                setFilteredRows(response.data)

                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Se ha eliminado exitosamente la agenda elegida.`
                })
            }

            // La tabla cierra/actualiza el modal usando este return
            return response
        } catch (error) {
            await LogErrors('DeleteAgenda - Agenda', error)
        }
    }

    const DeleteMultipleAgendas = async (_actionTypeOfModal, selectedRows) => {
        try {
            const resumen = {
                successful: [],
                wrongs: [],
                omitteds: []
            }

            let currentRows = [...allRowsRef.current]

            for (const agenda of selectedRows) {
                try {
                    // 1) IDs requeridos
                    const { idAgenda, idGroup, idCourse, idItem } = GetAgendaIdsFromRow(agenda)
                    
                    if (![idAgenda, idGroup, idCourse, idItem].every(n => Number.isInteger(n) && n > 0)) {
                        resumen.wrongs.push({ ...agenda, error: 'Faltan identificadores para eliminar' })
                        continue
                    }
                    
                    // 2) Omitir si ya no existe en la lista viva
                    if (!ExistsByAgendaId(currentRows, idAgenda)) {
                        resumen.omitteds.push({ ...agenda, reason: 'Ya no existe en el listado' })
                        continue
                    }
                    
                    // 3) Eliminar en BL y mantener lista al día
                    const resp = await BLDeleteAgenda(idAgenda, idGroup, idCourse, idItem, currentRows)
                    
                    if (resp.success) {
                        resumen.successful.push(agenda)
                        currentRows = resp.data // usar la versión actualizada
                    } else {
                        resumen.wrongs.push({ ...agenda, error: resp.message ?? 'Error desconocido' })
                    }
                } catch (error) {
                    resumen.wrongs.push({ ...agenda, error: error.message })
                }
            }

            // Actualizar la tabla con el último estado
            setAllRows(currentRows)
            setFilteredRows(currentRows)

            setMassiveSummary(resumen) // ← Usarás esto para mostrar el modal
            setShowMassiveSummaryModal(true)

            setActionTypeOfModal(_actionTypeOfModal)
        } catch (error) {
            await LogErrors('DeleteMultipleAgendas - Agenda', error)
        }
    }

    // Normaliza "HH:mm" -> "HH:mm:00"
    const toHHMMSS = (t) => {
        try {
            if (!t) return ''
            
            const p = t.split(':')
            if (p.length === 2) return `${p[0].padStart(2,'0')}:${p[1].padStart(2,'0')}:00`
            if (p.length === 3) return `${p[0].padStart(2,'0')}:${p[1].padStart(2,'0')}:${p[2].padStart(2,'0')}`
            
            return ''
        } catch (error) {
            
        }
    }

    const isValidISODate = (dateStr) => {
        try {
            if (typeof dateStr !== 'string') return false

            const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
            if (!m) return false
            
            const y = +m[1], mo = +m[2], d = +m[3]
            if (mo < 1 || mo > 12) return false
            
            const dim = [31, (y%4===0 && (y%100!==0 || y%400===0))?29:28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mo-1]
            
            return d >= 1 && d <= dim
        } catch (error) {
            
        }
    }

    const isValidTime = (t) => /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(t)

    const cmpDateTime = (d1, t1, d2, t2) => {
        try {
            const a = `${d1} ${t1.length===5?`${t1}:00`:t1}`
            const b = `${d2} ${t2.length===5?`${t2}:00`:t2}`
            
            return a < b ? -1 : (a > b ? 1 : 0)
        } catch (error) {
            
        }
    }

    // Máximo de oportunidades por tipo
    const getMaxOpsByType = (typeEval = '') => {
        const t = String(typeEval).trim().toLowerCase()
        if (t === 'parcial' || t === 'general') return 10
        if (t === 'diagnóstico' || t === 'diagnostico') return 1
        return 2 // final, extraordinario, etc.
    }

    const isValidOps = (ops, typeEval = '') => {
        const n = Number(ops)
        if (!Number.isInteger(n) || n < 1) return false
        return n <= getMaxOpsByType(typeEval)
    }

    const isDiagnostico = (typeEval = '') => String(typeEval).trim().toLowerCase() === 'diagnóstico' || String(typeEval).trim().toLowerCase() === 'diagnostico'

    const RescheduleToAgenda = async () => {
        try {
            if (!rescheduleAgendaDetails) return

            // 1) Tomar valores desde el estado del modal
            const idAgenda    = rescheduleAgendaDetails.id ?? rescheduleAgendaDetails.IdAgenda
            let startDateRaw  = rescheduleAgendaDetails.startDate || ''
            let finishDateRaw = rescheduleAgendaDetails.finishDate || ''
            const startHourRaw  = rescheduleAgendaDetails.startHour || ''
            const finishHourRaw = rescheduleAgendaDetails.finishHour || ''
            
            const typeEval = rescheduleAgendaDetails.typeEvaluation
            // preferimos el campo controlado; si no, currentOpportunities; si no, 1
            const opportunities = Number(
                rescheduleAgendaDetails?.opportunities ?? rescheduleAgendaDetails?.currentOpportunities ?? 1
            )

            // 2) Normalizar fechas a YYYY-MM-DD (usa tu helper existente)
            const startDate  = ConvertToInputDate(startDateRaw)   // asegura YYYY-MM-DD
            const finishDate = ConvertToInputDate(finishDateRaw)  // asegura YYYY-MM-DD
            
            // 3) Normalizar horas a HH:mm:ss
            const startHour  = toHHMMSS(startHourRaw)
            const finishHour = toHHMMSS(finishHourRaw)
            
            // 4) Validaciones mínimas
            if (!idAgenda) {
                console.error('Falta IdAgenda.')
                return { success: false, message: 'Falta IdAgenda.' }
            }
            if (!isValidISODate(startDate) || !isValidISODate(finishDate)) {
                console.error('Fecha inválida. Usa YYYY-MM-DD.')
                return { success: false, message: 'Fecha inválida. Usa YYYY-MM-DD.' }
            }
            if (!isValidTime(startHour) || !isValidTime(finishHour)) {
                console.error('Hora inválida. Usa HH:mm o HH:mm:ss.')
                return { success: false, message: 'Hora inválida. Usa HH:mm o HH:mm:ss.' }
            }
            if (cmpDateTime(startDate, startHour, finishDate, finishHour) >= 0) {
                console.error('La fecha/hora de inicio debe ser menor a la de fin.')
                return { success: false, message: 'Inicio debe ser estrictamente anterior a fin.' }
            }
            
            // Validación de oportunidades (según tipo)
            if (!isValidOps(opportunities, typeEval)) {
                const max = getMaxOpsByType(typeEval)
                const msg = isDiagnostico(typeEval)
                    ? 'La evaluación diagnóstica se puede presentar una sola vez.'
                    : `El número de oportunidades debe ser entre 1 y ${max}.`
                console.error(msg)
                return { success: false, message: msg }
            }
            
            // 5) Construir payload final
            const payload = {
                idAgenda: idAgenda,
                startDate,
                finishDate,
                startHour,
                finishHour,
                currentOpportunities: opportunities
            }
            
            // 6) Llamar al BL (según indicas, recibe sólo el objeto)
            const response = await BLRescheduleAgenda(payload, allRowsRef.current)
            
            if (response?.success) {
                // 7) Refrescar tabla con lo que devuelva el backend
                if (Array.isArray(response.data)) {
                    setAllRows(response.data)
                    setFilteredRows(response.data)
                }
                
                // 8) Limpiar controles/estado y cerrar modales
                setActionTypeOfModal(null)
                setRescheduleAgendaDetails(null)
                setRescheduleAgendaModal(false)
                setShowDetailsModal(false)
                
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Se ha reprogramado exitosamente la agenda elegida.`
                })

                return response
            } else {
                // Mantener modal abierto para que el usuario corrija y vuelva a intentar
                console.error(response?.message ?? 'No fue posible reagendar.')
                return { success: false, message: response?.message ?? 'No fue posible reagendar.' }
            }
        } catch (error) {
            await LogErrors('RescheduleToAgenda - Agenda', error)
        }
    }

    //--- Calls to API ----//
    


    //--- Confirm Modal ----//
    
    const ShowConfirmModal = async (actionType, agenda) => {
        try {
            if (actionType && agenda) {
                if (actionType === 3) {
                    setActionTypeOfModal(actionType)
                    setAgendaConfirmDetails(agenda)
                    setShowConfirmationModal(true)

                    setShowMassiveSummaryModal(null)
                    setRescheduleAgendaModal(null)
                    setShowDetailsModal(null)
                } else {
                    setActionTypeOfModal(null)
                    setAgendaConfirmDetails(null)
                    setShowConfirmationModal(false)

                    setShowMassiveSummaryModal(null)
                    setRescheduleAgendaModal(null)
                    setShowDetailsModal(null)
                    return
                }
            }
        } catch (error) {
            await LogErrors('ShowConfirmModal - Agenda', error)
        }
    }

    //--- Confirm Modal ----//
    
    

    //--- Details Modal ----//

    const ShowOrHideStepsButtons = async (index, length) => {
        try {
            setShowPreviousStepButton(index > 0)
            setShowNextStepButton(index < (length - 1))
        } catch (error) {
            await LogErrors('ShowOrHideStepsButtons - Agenda', error)
        }
    }

    const ShowAgendaDetails = async (_actionType = 1, agenda) =>{
        try {
            // Detalles completos desde allRows (como ya hacías)
            const tmpDetails = allRowsRef.current.find((row) => row.id === agenda.id)
            if (!tmpDetails) return

            if (_actionType === 1) {
                setAgendaDetails(tmpDetails)
            } else if (_actionType === 2) {
                setRescheduleAgendaDetails({
                    ...tmpDetails,
                    opportunities: Number(tmpDetails?.currentOpportunities) > 0 
                    ? Number(tmpDetails.currentOpportunities) 
                    : getMaxOpsByType(tmpDetails?.typeEvaluation) === 1 ? 1 : 1
                })
            }

            // Índice en la lista **visible**
            const idx = filteredRowsRef.current.findIndex(r => r.id === agenda.id)
            setCurrentIndexInFiltered(idx)

            await ShowOrHideStepsButtons(idx, filteredRowsRef.current.length)

            // Mostrar el modal correspondiente (sin cambios)
            if (_actionType === 1) {
                setShowConfirmationModal(null)
                setShowMassiveSummaryModal(null)
                setRescheduleAgendaModal(null)
                setShowDetailsModal(true)
            } else if (_actionType === 2) {
                setShowConfirmationModal(null)
                setShowMassiveSummaryModal(null)
                setShowDetailsModal(null)
                setRescheduleAgendaModal(true)
            }

            setActionTypeOfModal(_actionType)
        } catch (error) {
            await LogErrors('ShowAgendaDetails - Agenda', error)
        }
    }

    const PreviousStepButton = async () => {
        try {
            const list = filteredRowsRef.current
            if (!list || list.length === 0) return

            const newIndex = currentIndexInFiltered - 1
            if (newIndex < 0) return

            const targetId = list[newIndex]?.id
            if (!targetId) return

            const fullRow = allRowsRef.current.find(r => r.id === targetId)
            if (!fullRow) return

            if (actionTypeOfModal === 1) setAgendaDetails(fullRow)
            if (actionTypeOfModal === 2) setRescheduleAgendaDetails(fullRow)

            setCurrentIndexInFiltered(newIndex)
            await ShowOrHideStepsButtons(newIndex, list.length)

            if (actionTypeOfModal === 1) {
                setShowConfirmationModal(null); setShowMassiveSummaryModal(null); setRescheduleAgendaModal(null); setShowDetailsModal(true)
            } else if (actionTypeOfModal === 2) {
                setShowConfirmationModal(null); setShowMassiveSummaryModal(null); setShowDetailsModal(null); setRescheduleAgendaModal(true)
            }
        } catch (error) {
            await LogErrors('PreviousStepButton - Agenda', error)
        }
    }

    const NextStepButton = async () => {
        try {
            const list = filteredRowsRef.current
            if (!list || list.length === 0) return

            const newIndex = currentIndexInFiltered + 1
            if (newIndex > (list.length - 1)) return

            const targetId = list[newIndex]?.id
            if (!targetId) return

            const fullRow = allRowsRef.current.find(r => r.id === targetId)
            if (!fullRow) return

            if (actionTypeOfModal === 1) setAgendaDetails(fullRow)
            if (actionTypeOfModal === 2) setRescheduleAgendaDetails(fullRow)

            setCurrentIndexInFiltered(newIndex)
            await ShowOrHideStepsButtons(newIndex, list.length)

            if (actionTypeOfModal === 1) {
                setShowConfirmationModal(null); setShowMassiveSummaryModal(null); setRescheduleAgendaModal(null); setShowDetailsModal(true)
            } else if (actionTypeOfModal === 2) {
                setShowConfirmationModal(null); setShowMassiveSummaryModal(null); setShowDetailsModal(null); setRescheduleAgendaModal(true)
            }
        } catch (error) {
            await LogErrors('NextStepButton - Agenda', error)
        }
    }

    useEffect(() => {
        if (showDetailsModal || rescheduleAgendaModal) {
            // Si el id actual ya no está visible, cierra.
            const currentId = (actionTypeOfModal === 1 ? agendaDetails?.id : rescheduleAgendaDetails?.id)

            if (!filteredRows.some(r => r.id === currentId)) {
                setShowDetailsModal(false)
                setRescheduleAgendaModal(false)
            } else {
                // si sigue visible, actualiza el índice y botones
                const idx = filteredRows.findIndex(r => r.id === currentId)
                setCurrentIndexInFiltered(idx)
                ShowOrHideStepsButtons(idx, filteredRows.length)
            }
        }
    }, [filteredRows]) // eslint-disable-line

    //--- Details Modal ----//

    const handleRescheduleInputChange = async (field, value) => {
        try {
            setRescheduleAgendaDetails(prev => ({
                ...prev,
                [field]: value
            }))
        } catch (error) {
            await LogErrors('handleRescheduleInputChange - Agenda', error)
        }
    }

    const LoadPage = async () => {
        try {
            const response = await GetAgendas()
            
            if (response.success) {
                setAllRows(response.data)
                setFilteredRows(response.data)
                
                const tmpTableConfig = {
                    columns: ['Nombre', 'Curso', 'Grupo', 'Tipo', 'Fecha'],
                    rowKeys: ['itemName', 'courseName', 'groupName', 'typeEvaluation', 'date'],
                    rows: response.data,
                    actions: [
                        {
                            label: 'Detalles',
                            onClick: async (agendaRow) => {
                                await ShowAgendaDetails(1, agendaRow)
                            }
                        },
                        {
                            icon: 'bi bi-calendar',
                            label: 'Reprogramar',
                            onClick: async (agendaRow) => {
                                await ShowAgendaDetails(2, agendaRow)
                            }
                        },
                        {
                            icon: 'bi bi-trash',
                            onClick: async (agendaRow) => {
                                await ShowConfirmModal(3, agendaRow)
                            }
                        }
                    ],
                    bulkActions: [
                        {
                            label: 'Eliminar {n} evaluación(es)',
                            onClick: async (selectedRows) => {
                                await DeleteMultipleAgendas(3, selectedRows)
                            }
                        }
                    ],
                    selectionMessage: 'evaluación(es) seleccionada(s)'
                }

                setTableConfig(tmpTableConfig)
            } else {

            }
        } catch (error) {
            await LogErrors('LoadPage - Agenda', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        async function Validate() {
            const tmpSession = await ValidateSession()
            
            if (tmpSession.success) {
                if (tmpSession.data.user.userStatus === 2) {
                    await LoadPage()
                } else {
                    navigate('/confirm')
                }
            } else {
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        Validate()
    }, [])
    
    {/* NUEVO: construir campo combinado */}
    const displayRows = (filteredRows ?? []).map(r => ({
        ...r,
        //titleWithCourse: `${r.itemName ?? ''}\n${r.courseName ? 'Curso: ' + r.courseName : ''}`
    }))


    //useTawkChat(true) // Activamos el chat en esta página


    return (
        isLoading ? (
            <Loading />
        ) : (
            <>
                <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.INSTRUCTOR.AGENDA} page={WEBPAGES.INSTRUCTOR.AGENDA} role={ROLES.INSTRUCTOR}>
                    <section className='container Instructor__Agendas'>
                        <p className='Instructor__Title Instructor__Title--Center'>Agenda de evaluaciones</p>
                        {allRows && (
                            allRows.length > 0 && (
                                <p className='Instructor__Subtitle Instructor__Subtitle--Center'>Total de evaluaciones agendadas: {allRows.length}</p>
                            )
                        )}

                        <p className='Instructor__Agendas__CreateText'>Haz clic en el siguiente botón para agendar una <strong>nueva</strong> evaluación</p>
                        <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__CreateButton Instructor__Agendas__CreateButton' onClick={() => navigate('/instructor/agenda/create')}>Agendar NUEVA evaluación</button>

                        {allRows ? (
                            <>
                                {allRows.length === 0 ? (
                                    <p className='Instructor__Text--Center Instructor__SpaceUp'>Actualmente no hay evaluaciones agendadas.</p>
                                ) : (
                                    <>
                                        <p className='Instructor__Agendas__FiltersText'>Utiliza los <strong>filtros</strong> o el control <strong>buscar</strong> para encontrar las evaluaciones que agendaste previamente</p>

                                        {/* Filtros avanzados */}
                                        <div className='Instructor__Agendas__Filters'>
                                            {/* Línea de selects */}
                                            <div className='Instructor__Agendas__Filters__Row'>
                                                <select
                                                    className='Instructor__Agendas__Filters__Select'
                                                    value={filters.course || ''}
                                                    onChange={(e) => setFilters(f => ({ ...f, course: e.target.value || null }))}
                                                >
                                                    <option value=''>Curso (todos)</option>
                                                    {uniqueCourses.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>

                                                <select
                                                    className='Instructor__Agendas__Filters__Select'
                                                    value={filters.group || ''}
                                                    onChange={(e) => setFilters(f => ({ ...f, group: e.target.value || null }))}
                                                >
                                                    <option value=''>Grupo (todos)</option>
                                                    {uniqueGroups.map(g => <option key={g} value={g}>{g}</option>)}
                                                </select>

                                                <select
                                                    className='Instructor__Agendas__Filters__Select'
                                                    value={filters.type || ''}
                                                    onChange={(e) => setFilters(f => ({ ...f, type: e.target.value || null }))}
                                                >
                                                    <option value=''>Tipo (todos)</option>
                                                    {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>

                                                {/* Limpiar filtros */}
                                                <button
                                                    type='button'
                                                    className='Btn__Ztudia--PrimaryOutline Instructor__Agendas__Filters__Clear'
                                                    onClick={() => {
                                                        setTextRaw('')
                                                        setTextDebounced('')
                                                        setFilters({ course:null, group:null, type:null, dateFrom:'', dateTo:'' })
                                                        setQuickRange(null)
                                                        setFilteredRows(allRowsRef.current ?? [])
                                                    }}
                                                >
                                                    Limpiar filtros
                                                </button>
                                            </div>

                                            {/* Texto: va después de los selects; solo actualiza textRaw */}
                                            <Search value={textRaw} onChange={handleSearch} />
                                        </div>

                                        <div className='Instructor__Agendas__TableSummary text-center'>
                                            Mostrando <strong>{displayRows.length}</strong> {displayRows.length === 1 ? 'evaluación' : 'evaluaciones'}{allRows?.length ? <> de <strong>{allRows.length}</strong></> : ''}
                                        </div>

                                        <div>
                                            <Tables
                                                rows={displayRows}
                                                allRows={allRows}
                                                columns={['Nombre / Curso', 'Grupo', 'Tipo', 'Fecha']}
                                                rowKeys={['titleWithCourse', 'groupName', 'typeEvaluation', 'date']}
                                                sortKeys={['itemName', 'groupName', 'typeEvaluation', 'startSortValue']}
                                                actions={tableConfig.actions}
                                                bulkActions={tableConfig.bulkActions}
                                                selectionMessage={tableConfig.selectionMessage}
                                                extraClassName={'Instructor__Agendas__ContainerTable__Table'}
                                                sortStorageKey='agenda.sort'
                                                extraCellRenderers={{
                                                    // titleWithCourse: (r) => (
                                                    //     <>
                                                    //         <span className='Instructor__Agendas__ContainerTable__Table__TextColumn__Name'>{r.itemName ? `${r.itemName}` : ''}</span>
                                                    //         <span className='Instructor__Agendas__ContainerTable__Table__TextColumn__Course'>{r.courseName ? `Curso: ${r.courseName} nivel ${r.levelName}` : ''}</span>
                                                    //     </>
                                                    // )
                                                    titleWithCourse: (r) => (
                                                        <>
                                                            <span className='Instructor__Agendas__ContainerTable__Table__TextColumn__Name'>{r.itemName ? `${r.itemName}` : ''}</span>
                                                            <span className='Instructor__Agendas__ContainerTable__Table__TextColumn__Course'>
                                                                {r.courseName ? `Curso: ${r.courseName} ${r.levelName ? `nivel ${r.levelName}` : ''}`.trim() : ''}
                                                            </span>
                                                        </>
                                                    )
                                                }}
                                                searchQuery={textDebounced}
                                            />

                                        </div>
                                    </>
                                )}

                                {showDetailsModal && agendaDetails && (
                                    <Details
                                        stepButtons={true}
                                        actionToPreviousStepButton={() => PreviousStepButton()}
                                        showPreviousStepButton={showPreviousStepButton}
                                        actionToNextStepButton={() => NextStepButton()}
                                        showNextStepButton={showNextStepButton}
                                        showCloseModalButtonInContent={false}
                                        extraClassName={'Instructor__Agendas__Details'}
                                        setShowModal={setShowDetailsModal}
                                        setDetailsData={setAgendaDetails}
                                    >
                                        <div className='Instructor__Agendas__Details__Container'>
                                            <p className='Instructor__Agendas__Details__Title'>Detalles de la evaluación</p>

                                            {agendaDetails ? (
                                                <>
                                                    <p className='Instructor__Agendas__Details__Subtitle'>{agendaDetails.itemName && agendaDetails.itemName}</p>

                                                    {/* <p className='Instructor__Agendas__Details__Text Instructor__SpaceUp'>Curso: {agendaDetails.courseName && agendaDetails.courseName} {agendaDetails.levelName && agendaDetails.levelName}</p> */}
                                                    <p className='Instructor__Agendas__Details__Text Instructor__SpaceUp'>Curso: {agendaDetails.courseName} {agendaDetails.levelName ? `nivel ${agendaDetails.levelName}` : ''}</p>
                                                    <p className='Instructor__Agendas__Details__Text'>Grupo: {agendaDetails.groupName && agendaDetails.groupName}</p>
                                                    <p className='Instructor__Agendas__Details__Text'>Tipo: {agendaDetails.typeEvaluation && agendaDetails.typeEvaluation}</p>
                                                    <p className='Instructor__Agendas__Details__Text'>Fecha: {agendaDetails.date && agendaDetails.date}</p>
                                                    <p className='Instructor__Agendas__Details__Text'>No. de oportunidades: {agendaDetails.currentOpportunities && agendaDetails.currentOpportunities}</p>
                                                    
                                                    <div className='Instructor__Agendas__Details__ContainerButtons Instructor__SpaceUp'>
                                                        <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Agendas__Details__ContainerButtons__Button' onClick={() => ShowAgendaDetails(2, agendaDetails)}>Reprogramar evaluación</button>
                                                        <button type='button' className='Btn__Ztudia--DangerOutline Instructor__Agendas__Details__ContainerButtons__Button' onClick={() => ShowConfirmModal(3, agendaDetails)}>Eliminar evaluación</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p className='Instructor__Agendas__Details__Text'>No ha sido posible mostrar la información de la evaluación.</p>
                                                    <p className='Instructor__Agendas__Details__Text'>Intenta consultar su información nuevamente o inténtalo más tarde.</p>
                                                </>
                                            )}
                                        </div>
                                    </Details>
                                )}

                                {rescheduleAgendaModal && rescheduleAgendaDetails && (
                                    <Details
                                        stepButtons={true}
                                        actionToPreviousStepButton={() => PreviousStepButton()}
                                        showPreviousStepButton={showPreviousStepButton}
                                        actionToNextStepButton={() => NextStepButton()}
                                        showNextStepButton={showNextStepButton}
                                        showCloseModalButtonInContent={false}
                                        extraClassName={'Instructor__Agendas__RescheduleDetails'}
                                        setShowModal={setRescheduleAgendaModal}
                                        setDetailsData={setRescheduleAgendaDetails}
                                    >
                                        <div className='Instructor__Agendas__RescheduleDetails__Container'>
                                            <p className='Instructor__Agendas__RescheduleDetails__Title'>Reprogramar evaluación</p>
                                            
                                            {rescheduleAgendaDetails ? (
                                                <>
                                                    {/* <p className='Instructor__Agendas__RescheduleDetails__Subtitle'>{rescheduleAgendaDetails.itemName && rescheduleAgendaDetails.itemName}</p> */}
                                                    <p className='Instructor__Agendas__RescheduleDetails__Subtitle'>{rescheduleAgendaDetails.itemName && rescheduleAgendaDetails.itemName}</p>

                                                    <p className='Instructor__Agendas__RescheduleDetails__Text'>Grupo: {rescheduleAgendaDetails.groupName && rescheduleAgendaDetails.groupName}</p>
                                                    <p className='Instructor__Agendas__RescheduleDetails__Text'>Tipo: {rescheduleAgendaDetails.typeEvaluation && rescheduleAgendaDetails.typeEvaluation}</p>
                                                    <p className='Instructor__Agendas__RescheduleDetails__Text'>Fecha: {rescheduleAgendaDetails.date && rescheduleAgendaDetails.date}</p>
                                                    <p className='Instructor__Agendas__RescheduleDetails__Text'>No. de oportunidades: {rescheduleAgendaDetails.currentOpportunities && rescheduleAgendaDetails.currentOpportunities}</p>

                                                    <p className='Instructor__Agendas__RescheduleDetails__Text Instructor__Text--Center Instructor__SpaceUp'>¿En qué rango de fechas deseas que puedan iniciar su evaluación?</p>

                                                    <div className='Instructor__Agendas__RescheduleDetails__Datetime'>
                                                        <div className='Instructor__Agendas__RescheduleDetails__Datetime__Container'>
                                                            {/* <label className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Label'>Elige la fecha de inicio:</label> */}
                                                            <input
                                                                type='date'
                                                                className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Input'
                                                                value={ConvertToInputDate(rescheduleAgendaDetails.startDate) || ''}
                                                                onChange={(e) => handleRescheduleInputChange('startDate', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className='Instructor__Agendas__RescheduleDetails__Datetime__Container'>
                                                            {/* <label className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Label'>Elige la fecha de fin:</label> */}
                                                            <input
                                                                type='date'
                                                                className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Input'
                                                                value={ConvertToInputDate(rescheduleAgendaDetails.finishDate) || ''}
                                                                onChange={(e) => handleRescheduleInputChange('finishDate', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <p className='Instructor__Agendas__RescheduleDetails__Text Instructor__Text--Center Instructor__SpaceUp'>¿En qué rango de horarios deseas que puedan iniciar su evaluación?</p>

                                                    <div className='Instructor__Agendas__RescheduleDetails__Datetime'>
                                                        <div className='Instructor__Agendas__RescheduleDetails__Datetime__Container'>
                                                            {/* <label className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Label'>Hora de inicio:</label> */}
                                                            <input
                                                                type='time'
                                                                className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Input'
                                                                value={rescheduleAgendaDetails.startHour || ''}
                                                                onChange={(e) => handleRescheduleInputChange('startHour', e.target.value)}
                                                            />
                                                        </div>

                                                        <div className='Instructor__Agendas__RescheduleDetails__Datetime__Container'>
                                                            {/* <label className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Label'>Hora de fin:</label> */}
                                                            <input
                                                                type='time'
                                                                className='Instructor__Agendas__RescheduleDetails__Datetime__Container__Input'
                                                                value={rescheduleAgendaDetails.finishHour || ''}
                                                                onChange={(e) => handleRescheduleInputChange('finishHour', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <p className='Instructor__Agendas__RescheduleDetails__Text Instructor__Text--Center Instructor__SpaceUp'>¿Cuántas veces podría presentar un Ztudiante esta evaluación?</p>

                                                    {isDiagnostico(rescheduleAgendaDetails.typeEvaluation) ? (
                                                        <p className='Instructor__Text--Center'>
                                                            {Number(rescheduleAgendaDetails?.opportunities ?? rescheduleAgendaDetails?.currentOpportunities ?? 1)}
                                                        </p>
                                                    ) : (
                                                        <select className='Instructor__Agendas__RescheduleDetails__List' value={Number(rescheduleAgendaDetails?.opportunities ?? rescheduleAgendaDetails?.currentOpportunities ?? 1)} onChange={(e) => handleRescheduleInputChange('opportunities', Number(e.target.value))}>
                                                            {Array.from(
                                                                { length: getMaxOpsByType(rescheduleAgendaDetails?.typeEvaluation) },
                                                                (_, i) => <option key={i+1} value={i+1}>{i+1}</option>
                                                            )}
                                                        </select>
                                                    )}

                                                    <div className='Instructor__Agendas__RescheduleDetails__ContainerButtons Instructor__SpaceUp'>
                                                        <button type='button' className='Btn__Ztudia--PrimaryOutline Instructor__Agendas__RescheduleDetails__ContainerButtons__Button' onClick={() => RescheduleToAgenda()}>Guardar cambios</button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <p className='Instructor__Agendas__RescheduleDetails'>No ha sido posible mostrar la información de la evaluación.</p>
                                                    <p className='Instructor__Agendas__RescheduleDetails'>Intenta consultar su información nuevamente o inténtalo más tarde.</p>
                                                </>
                                            )}
                                        </div>
                                    </Details>
                                )}

                                {showConfirmationModal && (
                                    agendaConfirmDetails && (
                                        actionTypeOfModal === 3 && (
                                            <Confirm
                                                message={`¿Estás segur@ de eliminar la evaluación agendada ${agendaConfirmDetails.itemName}?`}
                                                actionToYesButton={DeleteAgenda}
                                                textYesButton={'Sí, eliminar evaluación'}
                                                textNoButton={'No'}
                                                setShowModal={setShowConfirmationModal}
                                                setConfirmData={setAgendaConfirmDetails}
                                            />
                                        )
                                    )
                                )}

                                {showMassiveSummaryModal && massiveSummary && (
                                    <Details
                                        stepButtons={false}
                                        showCloseModalButtonInContent={true}
                                        extraClassName={'Modal__Layout--Summary'}
                                        setShowModal={setShowMassiveSummaryModal}
                                        setDetailsData={setMassiveSummary}
                                    >
                                        <div className='Modal__Layout--Summary__Container'>
                                            <p className='Modal__Layout--Summary__Title'>Resumen {actionTypeOfModal && actionTypeOfModal === 3 && 'de evaluaciones agendadas eliminadas'}</p>

                                            {massiveSummary ? (
                                                <>
                                                    {massiveSummary.successful.length > 0 && (
                                                        <>
                                                            <p className='Modal__Layout--Summary__Category'><i className='bi bi-check-circle'></i> {actionTypeOfModal === 3 && 'Evaluaciones agendadas eliminadas:'}</p>
                                                            <div className='Modal__Layout--Summary__TextContainer'>
                                                            {massiveSummary.successful.map((success,index) => (
                                                                <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{success.itemName}</p>
                                                            ))}
                                                            </div>
                                                        </>
                                                    )}

                                                    {massiveSummary.wrongs.length > 0 && (
                                                        <>
                                                            <p className='Modal__Layout--Summary__Category'><i className="bi bi-x-circle"></i> {actionTypeOfModal === 3 && 'Error al eliminarla(s):'}</p>
                                                            <div className='Modal__Layout--Summary__TextContainer'>
                                                            {massiveSummary.wrongs.map((wrong,index) => (
                                                                <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{wrong.itemName}</p>
                                                            ))}
                                                            </div>
                                                        </>
                                                    )}

                                                    {massiveSummary.omitteds.length > 0 && (
                                                        <>
                                                            <p className='Modal__Layout--Summary__Category'><i className="bi bi-skip-end-btn"></i> Omitidas:</p>
                                                            <div className='Modal__Layout--Summary__TextContainer'>
                                                            {massiveSummary.omitteds.map((omitted,index) => (
                                                                <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{omitted.itemName}</p>
                                                            ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <p className='Modal__Layout--Summary__Text'>No ha sido posible mostrar el resumen de lo realizado.</p>
                                                </>
                                            )}
                                        </div>
                                    </Details>
                                )}
                            </>
                        ) : (
                            <p className='Instructor__Text--Center'>No ha sido posible mostrar las evaluaciones agendadas. Intenta actualizar la página o inténtalo más tarde.</p>
                        )}
                    </section>
                </InstructorBase>
            </>
        )
    )
}

export { Agenda };